// ...existing code...
import React, { useEffect, useState } from "react";
import ApiClient from "../services/fuelandmoreapi";
import BinIcon from "./BinIcon";
import { config } from "../config";
import type { TUser } from "@/types";
import { FeedbackText } from "./FeedbackText";
import UserForm from "./UserForm";
import "../global.css";
import "./AccountManagerList.css";

interface AccountManagerListProps {}

const UserList: React.FC<AccountManagerListProps> = ({}) => {
  const [users, setUsers] = useState<TUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // unified modal for add/edit
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<TUser | null>(null); // user being edited or added
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false); // track add/edit mode
  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const apiClient = new ApiClient(apiUrl);
        const response = await apiClient.getAccountManagers();

        setUsers(response);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [apiUrl]);

  // handleUserAdded removed, unified modal logic below

  const handleDelete = async (email: string) => {
    setConfirmDelete(null);

    const deleteResponse = await new ApiClient(apiUrl).deleteAccountManager(
      email
    );
    if (deleteResponse.status < 200 || deleteResponse.status >= 300) {
      setError(
        deleteResponse.data?.message || "Failed to delete account manager."
      );
      return;
    }

    // Refresh users after deleting
    const response = await new ApiClient(apiUrl).getAccountManagers();
    setUsers(response);
  };

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div className="user-list-container">
      <h3>Account Managers</h3>

      <table className="user-list-table">
        <thead>
          <tr>
            <th>Given Name</th>
            <th>Family Name</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Job Title</th>
            <th>Company</th>
            <th>Country</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.email}>
              <td>{user.givenName}</td>
              <td>{user.familyName}</td>
              <td>{user.email}</td>
              <td>{user.phoneNumber}</td>
              <td>{user.jobTitle}</td>
              <td>{user.company}</td>
              <td>{user.country}</td>
              <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                <button
                  className="edit-btn"
                  title="Edit Account Manager"
                  onClick={() => {
                    setEditUser(user);
                    setIsEditMode(true);
                    setShowModal(true);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "black",
                    display: "inline-block",
                    margin: "0 auto",
                  }}
                >
                  {/* Inline SVG pencil icon, always black */}
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 17.25V21h3.75l11.06-11.06a1 1 0 0 0 0-1.41l-2.24-2.24a1 1 0 0 0-1.41 0L3 17.25z"
                      stroke="black"
                      strokeWidth="2"
                      fill="none"
                    />
                    <path
                      d="M14.06 7.94l2.24 2.24"
                      stroke="black"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </td>
              <td style={{ textAlign: "center" }}>
                <button
                  className="bin-btn"
                  title="Delete Account Manager"
                  onClick={() => setConfirmDelete(user.email)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <BinIcon color="black" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* UserForm modal for add/edit */}
      {showModal && editUser && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ width: "480px", maxWidth: "95%" }}
          >
            <h4>
              {isEditMode ? "Edit Account Manager" : "Add Account Manager"}
            </h4>
            <UserForm
              initialUser={editUser}
              mode={isEditMode ? "edit" : "add"}
              loading={editLoading}
              error={error}
              disableEmail={
                isEditMode &&
                editUser.email !== "" &&
                users.some((u) => u.email === editUser.email)
              }
              onSave={async (user) => {
                setEditLoading(true);
                setError(null);
                try {
                  const apiClient = new ApiClient(apiUrl);
                  if (!isEditMode) {
                    // Add
                    const response = await apiClient.addAccountManager(user);
                    if (response.status < 200 || response.status >= 300) {
                      setError(
                        response.data?.message ||
                          "Failed to add account manager."
                      );
                    } else {
                      const updatedUsers = await apiClient.getAccountManagers();
                      setUsers(updatedUsers);
                      setShowModal(false);
                      setEditUser(null);
                    }
                  } else {
                    // Edit
                    const response = await apiClient.updateAccountManager(user);
                    if (response.status < 200 || response.status >= 300) {
                      setError(
                        response.data?.message ||
                          "Failed to update account manager."
                      );
                    } else {
                      const updatedUsers = await apiClient.getAccountManagers();
                      setUsers(updatedUsers);
                      setShowModal(false);
                      setEditUser(null);
                    }
                  }
                } catch (err) {
                  setError(
                    !isEditMode
                      ? "Failed to add account manager."
                      : "Failed to update account manager."
                  );
                } finally {
                  setEditLoading(false);
                }
              }}
              onCancel={() => {
                setShowModal(false);
                setEditUser(null);
              }}
            />
          </div>
        </div>
      )}

      {error && <FeedbackText message={error} />}

      <div className="add-user-button-container">
        <button
          className="primary-button"
          onClick={() => {
            setShowModal(true);
            setIsEditMode(false);
            setEditUser({
              givenName: "",
              familyName: "",
              email: "",
              phoneNumber: "",
              jobTitle: "",
              company: "",
              country: "",
              whatsAppId: "",
              appSource: undefined,
            });
          }}
        >
          + Add Account Manager
        </button>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Are you sure you want to delete account manager{" "}
              <b>{confirmDelete}</b>?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={() => handleDelete(confirmDelete)}
              >
                Yes
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
